// Ported from nenda-autoplayer's src/lib/state/actions.ts. Logic verbatim
// throughout; only imports/types changed:
//   - `NendaApiClient` -> `ScheduleClient` (src/tv/signage/foyerScheduleClient.ts)
//   - `NendaLogClient` -> `LogClient` (src/tv/signage/webLogClient.ts)
//   - `DeviceController` -> `WebDeviceController` (src/tv/signage/webDeviceController.ts)
//   - `AutoplayerMachineContext`/`PlaybackContextType` from the extracted ./context.ts
//   - `devLogger`/schedule-utils from the already-ported signage-engine files
import { Session } from "../types/Session";
import { AutoplayerMachineContext, PlaybackContextType } from "./context";
import {
  AssetCompleteEvent,
  AssetCompleteStatus,
  FetchAdsDone,
} from "./events";
import devLogger from "../playback/devLogger";
import type { ScheduleClient } from "../../signage/foyerScheduleClient";
import {
  getTruncatedTimeString,
  getCurrentContentPlaylistId,
} from "./scheduleUtils";
import { skewedNow } from "./clockSkew";
import {
  MediaAsset,
  MediaType,
  ScheduledCommand,
  ScheduleType,
} from "../types/Schedule";
import { AdItem, AdSlot } from "../types/AdSlot";
import { AssetType, LogLevel, type NendaLogEntry } from "../types/LogEntry";
import type { LogClient } from "../../signage/webLogClient";
import { StateValue } from "xstate";
import type WebDeviceController from "../../signage/webDeviceController";

const REWIND_AFTER_BREAK_IN_MS = 1000;

export const evaluateSchedule = (
  context: AutoplayerMachineContext
): Partial<AutoplayerMachineContext> => {
  if (!context.schedule) return context;

  // DELTA FROM UPSTREAM: `skewedNow()` (clockSkew.ts), not a bare `new
  // Date()` - see that file's own doc comment. Default 0ms skew makes this
  // an exact no-op against upstream's own behavior.
  const now = getTruncatedTimeString(skewedNow());

  const marker = context.schedule?.slotSchedule[now];

  if (!marker) {
    return {
      ...context,
      playback: {
        ...context.playback,
        activeSlot: undefined,
      },
    };
  }

  if (marker.type === ScheduleType.Ad) {
    return {
      ...context,
      playback: {
        ...context.playback,
        signage: {
          currentIndex: 0,
          currentSignagePlaylist: [],
        },
        activeSlot: marker,
        ad: {
          currentIndex: 0,
          adslot: undefined,
        },
      },
    };
  }

  if (marker.type === ScheduleType.Signage) {
    return {
      ...context,
      playback: {
        ...context.playback,
        ad: {
          currentIndex: 0,
          adslot: undefined,
        },
        activeSlot: marker,
        signage: {
          currentIndex: 0,
          currentSignagePlaylist: [],
        },
      },
    };
  }

  return context;
};

export const runCommands = (
  context: AutoplayerMachineContext,
  deviceController: WebDeviceController,
  logClient: LogClient
): void => {
  if (!context.schedule?.commands?.length) {
    return;
  }

  context.schedule.commands.forEach((command) => {
    switch (command) {
      case ScheduledCommand.REBOOT:
        try {
          deviceController.rebootDevice();
          log({
            client: logClient,
            context,
            component: "runCommands",
            message: "Rebooting device",
            level: LogLevel.INFO,
          });
        } catch (error) {
          log({
            client: logClient,
            context,
            component: "runCommands",
            message: `Failed to reboot device: ${String(error)}`,
            level: LogLevel.SEVERE,
          });
        }
        break;

      case ScheduledCommand.LOGOUT:
        try {
          deviceController.logOut();
          log({
            client: logClient,
            context,
            component: "runCommands",
            message: "Logging out",
            level: LogLevel.INFO,
          });
        } catch (error) {
          log({
            client: logClient,
            context,
            component: "runCommands",
            message: `Failed to logout: ${String(error)}`,
            level: LogLevel.SEVERE,
          });
        }
        break;

      default:
        log({
          client: logClient,
          context,
          component: "runCommands",
          message: `Unknown command: ${command}`,
          level: LogLevel.WARNING,
        });
        break;
    }
  });
};

export const changeContent = (
  context: AutoplayerMachineContext
): Partial<AutoplayerMachineContext> => {
  if (!context.schedule?.contentSchedule) return context;

  const playlistId = getCurrentContentPlaylistId(context.schedule);
  if (!playlistId) return context;

  const playlist = context.schedule.playlists[playlistId];

  if (!playlist) return context;

  if (playlist.channels && playlist.channels.length > 0) {
    const channel = context.schedule.channels.find(
      (channel) => channel.id === playlist.channels![0]
    );

    if (!channel) return context;

    return {
      ...context,
      playback: {
        ...context.playback,
        content: {
          currentPlaylistId: playlistId,
          currentContent: channel,
          currentIndex: 0,
          currentAssetStartTime: undefined,
          remainingDuration: undefined,
        },
      },
    };
  }

  if (playlist.signagePlaylist) {
    const signagePlaylist = context.schedule.signagePlaylists.find(
      (spl) => spl.id === playlist.signagePlaylist
    );

    if (!signagePlaylist) return context;

    return {
      ...context,
      playback: {
        ...context.playback,
        content: {
          currentPlaylistId: playlistId,
          currentContent: signagePlaylist,
          currentIndex: 0,
          currentAssetStartTime: undefined,
          remainingDuration: undefined,
        },
      },
    };
  }

  return context;
};

export const playNextAsset = (
  context: AutoplayerMachineContext,
  type: ScheduleType
): Partial<AutoplayerMachineContext> => {
  if (!context.playback.content) return context;

  const typeKey: keyof PlaybackContextType =
    type.toLowerCase() as keyof PlaybackContextType;

  return {
    ...context,
    playback: {
      ...context.playback,
      [typeKey]: {
        ...context.playback[typeKey],
        currentIndex: context.playback[typeKey].currentIndex + 1,
      },
    },
  };
};

export const calculatePlaybackTimes = (
  context: AutoplayerMachineContext
): Partial<AutoplayerMachineContext> => {
  if (!context.playback.content) return context;

  const remainingDuration =
    context.playback.content.remainingDuration ||
    context.playback.content.currentContent?.items[
      context.playback.content.currentIndex
    ]?.durationInSeconds;

  const prevElapsed = context.playback.content.elapsedTime || 0;
  const elapsedSinceLastBreak =
    (Date.now() - (context.playback.content.currentAssetStartTime || 0)) / 1000;

  const elapsed =
    prevElapsed + elapsedSinceLastBreak - REWIND_AFTER_BREAK_IN_MS / 1000;

  const newDuration = Math.ceil(
    Math.max(0, Math.round(remainingDuration || 10) - elapsed)
  );

  return {
    playback: {
      ...context.playback,
      content: {
        ...context.playback.content,
        remainingDuration: newDuration,
        elapsedTime: Math.floor(elapsed),
      },
    },
  };
};

export const restartContentPlaylist = (
  context: AutoplayerMachineContext
): Partial<AutoplayerMachineContext> => {
  return {
    playback: {
      ...context.playback,
      content: {
        ...context.playback.content,
        currentIndex: 0,
        currentAssetStartTime: undefined,
        remainingDuration: undefined,
      },
    },
  };
};

export const recordAssetStartTime = (
  context: AutoplayerMachineContext
): Partial<AutoplayerMachineContext> => {
  return {
    playback: {
      ...context.playback,
      content: {
        ...context.playback.content,
        currentAssetStartTime: Date.now(),
      },
    },
  };
};

export const resetTimes = (
  context: AutoplayerMachineContext
): Partial<AutoplayerMachineContext> => {
  return {
    playback: {
      ...context.playback,
      content: {
        ...context.playback.content,
        currentAssetStartTime: undefined,
        remainingDuration: undefined,
        elapsedTime: 0,
      },
    },
  };
};

export const getSchedule = async (client: ScheduleClient, session: Session) => {
  return client.getScreenSchedule(session);
};

/** Confirmed unreachable, see ScheduleClient's own doc comment - `Promise<never>`
 *  return propagates from `client.getAdSlot()`. */
export const getAdSlot = async (
  client: ScheduleClient,
  session: Session
): Promise<AdSlot> => {
  return client.getAdSlot(session);
};

/** Transitively unreachable - only ever called from within the Ad-playback
 *  states `getAdSlot` above proves unreachable. */
export const registerAdView = async (
  context: AutoplayerMachineContext,
  client: ScheduleClient
) => {
  const ad = context.playback.ad.adslot?.ads[context.playback.ad.currentIndex];

  if (!ad) return;

  return client.registerAdView(
    context.session,
    ad.renderPixel,
    ad.adnuntiusAdId
  );
};

/** Reachable but safe to reject - see ScheduleClient's own doc comment on
 *  `sessionAuth`: `Initializing`'s RedBee-auth state continues on error. */
export const authenticateRedBeeUser = async (
  client: ScheduleClient,
  session: Session
) => {
  const dbg =
    typeof (window as unknown as { dbg?: (s: string) => void }).dbg ===
    "function"
      ? (window as unknown as { dbg: (s: string) => void }).dbg
      : (s: string) => console.log(s);
  try {
    dbg(
      "[auth-actor] start session=" +
        JSON.stringify({
          premiseId: session?.premiseId,
          screenId: session?.screenId,
        })
    );
    const result = await client.sessionAuth(session);
    dbg("[auth-actor] success");
    return result;
  } catch (e) {
    dbg("[auth-actor] ERROR: " + (e instanceof Error ? e.message : String(e)));
    throw e;
  }
};

export const setAdPlaylist = (
  context: AutoplayerMachineContext,
  event: FetchAdsDone
): Partial<AutoplayerMachineContext> => {
  return {
    playback: {
      ...context.playback,
      ad: {
        currentIndex: 0,
        adslot: event.output,
      },
    },
  };
};

export const setSignageContext = (
  context: AutoplayerMachineContext
): Partial<AutoplayerMachineContext> => {
  if (!context.playback.activeSlot) return context;
  if (context.playback.activeSlot.type !== ScheduleType.Signage) return context;

  const playlist =
    context.schedule?.playlists[context.playback.activeSlot.playlist];

  if (!playlist) return context;

  const mediaAssets = playlist.assets;

  if (!mediaAssets?.length) return context;

  return {
    playback: {
      ...context.playback,
      signage: {
        currentIndex: 0,
        currentSignagePlaylist: mediaAssets,
      },
    },
  };
};

export const checkConnectivity = async (client: ScheduleClient) => {
  const response = await client.ping();
  devLogger.log("Connectivity check response", response);
  if (response.status === 200) {
    return true;
  } else {
    throw new Error("Connectivity check failed with status " + response.status);
  }
};

export const logPlay = (
  client: LogClient,
  context: AutoplayerMachineContext,
  type: ScheduleType
) => {
  const typeKey: keyof PlaybackContextType =
    type.toLowerCase() as keyof PlaybackContextType;

  const index = context.playback[typeKey].currentIndex;
  let currentAsset;

  switch (type) {
    case ScheduleType.Content:
      currentAsset = context.playback.content.currentContent?.items[index];
      if (!currentAsset) return;
      logContentPlay(client, context, currentAsset);
      return;
    case ScheduleType.Ad:
      currentAsset = context.playback.ad.adslot?.ads[index];
      if (!currentAsset) return;
      logAdPlay(client, context, currentAsset);
      return;
    case ScheduleType.Signage:
      currentAsset = context.playback.signage.currentSignagePlaylist[index];
      if (!currentAsset) return;
      logSignagePlay(client, context, currentAsset);
      return;
    default:
      return;
  }
};

export const logAdPlay = (
  client: LogClient,
  context: AutoplayerMachineContext,
  adItem: AdItem
) => {
  const nendaLogEntry: NendaLogEntry = {
    roomId: context.session.screenId,
    premiseId: context.session.premiseId,
    component: "playAd",
    currentAssetId: adItem.adnuntiusAdId,
    currentAssetName: adItem.adnuntiusLineItemId,
    currentAssetType: AssetType.Advertisement,
    message: JSON.stringify(adItem),
    level: LogLevel.INFO,
  };

  client.sendLogEntry(nendaLogEntry);
};

export const logSignagePlay = (
  client: LogClient,
  context: AutoplayerMachineContext,
  mediaAsset: MediaAsset
) => {
  const nendaLogEntry: NendaLogEntry = {
    roomId: context.session.screenId,
    premiseId: context.session.premiseId,
    component: "playSignage",
    currentAssetId: mediaAsset.id,
    currentAssetName: mediaAsset.name,
    message: JSON.stringify(mediaAsset),
    currentAssetType: AssetType.Signage,
    level: LogLevel.INFO,
  };

  client.sendLogEntry(nendaLogEntry);
};

export const logContentPlay = (
  client: LogClient,
  context: AutoplayerMachineContext,
  content: MediaAsset
) => {
  const assetType = content.type;

  const isVideo =
    assetType === MediaType.Video || assetType === MediaType.LinearChannel;

  const message = isVideo
    ? `[StateMachine]: Init play for content at position ${context.playback.content.elapsedTime}`
    : `[StateMachine]: Init play for content with remaining duration ${context.playback.content.remainingDuration || content.durationInSeconds}`;

  const nendaLogEntry: NendaLogEntry = {
    roomId: context.session.screenId,
    premiseId: context.session.premiseId,
    component: "playContent",
    currentAssetId: content.id,
    currentAssetName: content.name,
    message: message,
    currentAssetType: AssetType.ContentChannel,
    level: LogLevel.INFO,
  };

  client.sendLogEntry(nendaLogEntry);
};

export const logContentPlaylistPlay = (
  client: LogClient,
  context: AutoplayerMachineContext
) => {
  const content = context.playback.content.currentContent;

  if (!content) return;

  const messagePayload = {
    ...content,
    items: undefined, // It could be hundreds of items in a playlist. We don't need to log them all.
    numberOfItems: content.items.length,
  };

  const nendaLogEntry: NendaLogEntry = {
    roomId: context.session.screenId,
    premiseId: context.session.premiseId,
    component: "playContentPlaylist",
    currentAssetId: content.id,
    currentAssetName: content.name,
    message: JSON.stringify(messagePayload),
    currentAssetType: AssetType.ContentChannel,
    level: LogLevel.INFO,
  };

  client.sendLogEntry(nendaLogEntry);
};

export const logPing = (
  client: LogClient,
  context: AutoplayerMachineContext,
  currentState: StateValue
) => {
  const nendaPingEntry: NendaLogEntry = {
    roomId: context.session.screenId,
    premiseId: context.session.premiseId,
    message: JSON.stringify(currentState),
    component: "Ping",
    level: LogLevel.INFO,
  };

  client.sendLogEntry(nendaPingEntry);
};

export const logAdView = (
  client: LogClient,
  context: AutoplayerMachineContext
) => {
  const ad = context.playback.ad.adslot?.ads[context.playback.ad.currentIndex];

  if (!ad) return;

  const nendaLogEntry: NendaLogEntry = {
    roomId: context.session.screenId,
    premiseId: context.session.premiseId,
    component: "register adView",
    currentAssetId: ad.adnuntiusAdId,
    currentAssetName: ad.adnuntiusLineItemId,
    currentAssetType: AssetType.Advertisement,
    level: LogLevel.INFO,
  };

  client.sendLogEntry(nendaLogEntry);
};

export const logAdPlaybackError = (
  client: LogClient,
  context: AutoplayerMachineContext,
  error: Error
) => {
  const ad = context.playback.ad.adslot?.ads[context.playback.ad.currentIndex];
  if (!ad) return;

  const nendaLogEntry: NendaLogEntry = {
    roomId: context.session.screenId,
    premiseId: context.session.premiseId,
    component: "Ad playback failed",
    currentAssetId: ad.adnuntiusAdId,
    currentAssetName: ad.adnuntiusLineItemId,
    currentAssetType: AssetType.Advertisement,
    message: error.message,
    level: LogLevel.SEVERE,
  };
  client.sendLogEntry(nendaLogEntry);
};

export const logContentAssetComplete = (
  client: LogClient,
  context: AutoplayerMachineContext,
  event: AssetCompleteEvent
) => {
  const isError = event.status === AssetCompleteStatus.ERROR;
  const component = isError ? "Playback error" : "Playback";
  const message = isError
    ? `Asset ${event.asset?.name} (${event.asset?.id})  playback failed: ${event.error ?? "unknown error"}`
    : `Asset ${event.asset?.name} (${event.asset?.id}) completed: ${event.status}`;
  const level = isError ? LogLevel.SEVERE : LogLevel.INFO;

  log({
    client,
    context,
    component,
    message,
    level,
    asset: event.asset,
  });
};

export interface LogParams {
  client: LogClient;
  context: AutoplayerMachineContext;
  component: string;
  message?: string;
  level?: LogLevel;
  asset?: MediaAsset;
}

export const log = ({
  client,
  context,
  component,
  message,
  level = LogLevel.INFO,
  asset,
}: LogParams) => {
  const nendaLogEntry: NendaLogEntry = {
    roomId: context.session.screenId,
    premiseId: context.session.premiseId,
    component,
    message,
    level,
    currentAssetId: asset?.id,
    currentAssetName: asset?.name,
  };

  client.sendLogEntry(nendaLogEntry);
};
